import api from './api'
import {
  BillingAddress,
  NextPayout,
  SheltersNft,
  User,
  getBillingAddressesResponse,
  UserInfo,
  UserWallet,
  UserPreferences,
} from '../types/User/User'
import { apiMultiversx } from './api-multiversx'
import { PaginatedResponse, PostAuthEmailResponse } from '../types/APIResponse'
import {
  getUserPayoutMethodsResponse,
  PayoutCurrency,
  ResponseGetPayoutsHistory,
  UserPayoutMethod,
} from '../types/Payout'
import {
  CreateOrderResult,
  GetOrdersResult,
  OrderStatus,
} from '../types/Order/Order'
import { getBaseUrl } from '../lib/utils/language'
import { CurrencyEnum } from '../types/Currency'
import { GetUserSessionResult } from '../types/User/UserSession'
import { Language } from '../types/Language'
import { CreateRevolutIban } from '../types/User/UserPayoutMethod/Revolut/CreateRevolutIban'
import { RevolutDetails } from '../types/User/UserPayoutMethod/Revolut/RevolutDetails'
import { MePayout } from '../types/Payout/MePayout'
import { Daterange } from '../types/Daterange'
import { UserWalletType } from '../types/Wallet/Wallet'
import { EsdtToken } from '../types/MultiversX/Esdt'
import {
  CanWithdraw,
  CreateBankTransferWithdraw,
} from '../types/Balance/Withdraw'
import { InitVerificationResult } from '../types/User/UserVerification'

const UserService = {
  async registerWithEmail(
    email: string,
    password: string,
    invitationSlug?: string,
  ): Promise<void> {
    const payload: {
      email: string
      password: string
      invitationSlug?: string
    } = {
      email,
      password,
    }

    if (invitationSlug) {
      payload.invitationSlug = invitationSlug
    }

    const { data } = await api.post('/v1/auth/register/email', payload)
  },
  async loginWithEmail(
    email: string,
    password: string,
  ): Promise<PostAuthEmailResponse> {
    const { data } = await api.post('/v1/auth/login/email', {
      email,
      password,
    })
    return data
  },
  async logout(): Promise<void> {
    await api.post('/v1/auth/logout')
  },
  async confirmEmail(email: string, token: string): Promise<any> {
    return api.post('/v1/auth/confirm-email', { email, token })
  },
  async forgotPassword(email: string): Promise<any> {
    const { data } = await api.post('/v1/auth/reset-password', { email })
    return data
  },
  async changePassword(
    userId: string,
    resetToken: string,
    password: string,
  ): Promise<any> {
    const { data } = await api.post('/v1/auth/change-password', {
      userId,
      resetToken,
      password,
    })
    return data
  },
  async getWallets(): Promise<UserWallet[]> {
    const { data } = await api.get('/v1/users/me/wallets')
    return data
  },
  async deleteWallet(walletId: string): Promise<void> {
    await api.delete(`/v1/users/me/wallets/${walletId}`)
  },
  async createWallet(
    address: string,
    signature: string,
    authToken: string,
    name: string,
    walletType: UserWalletType,
  ): Promise<any> {
    const { data } = await api.post('/v1/users/me/wallets', {
      address,
      signature,
      authToken,
      name,
      walletType,
    })
    return data
  },
  async createWalletV2(
    accessToken: string,
    name: string,
    walletType: UserWalletType,
    backup?: string,
  ): Promise<any> {
    const { data } = await api.post('/v2/users/me/wallets', {
      accessToken,
      name,
      walletType,
      backup,
    })
    return data
  },
  /**
   * @deprecated
   */
  async createWalletless(): Promise<UserWallet> {
    const { data } = await api.post('/v1/users/me/wallets/walletless')
    return data
  },
  async getSessions(): Promise<GetUserSessionResult> {
    const { data } = await api.get('/v1/users/me/sessions')
    return data
  },
  async destroySession(sessionId: string): Promise<any> {
    await api.delete(`/v1/users/me/sessions/${sessionId}`)
  },
  async getUser(): Promise<User> {
    const { data } = await api.get('/v1/users/me')
    return data
  },
  async updateUser(userInfo: UserInfo): Promise<User> {
    const { data } = await api.put('/v1/users/me', userInfo)
    return data
  },
  async getUserPreferences(): Promise<UserPreferences> {
    const { data } = await api.get('/v1/users/me/preferences')
    return data
  },
  async updatePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<any> {
    return api.post('/v1/users/me/password', { currentPassword, newPassword })
  },
  async getNfts(
    address: string,
    identifiers: string[],
  ): Promise<SheltersNft[]> {
    const { data } = await apiMultiversx.get('/accounts/' + address + '/nfts', {
      params: { identifiers: identifiers.join(',') },
    })
    return data.map(({ identifier, ticker, nonce, balance }: any) => ({
      identifier,
      ticker,
      nonce,
      balance: parseInt(balance),
    }))
  },
  async getTokens(address: string): Promise<EsdtToken[]> {
    const { data } = await apiMultiversx.get<EsdtToken[]>(
      '/accounts/' + address + '/tokens',
    )
    return data
  },
  async getNextYields(nfts: SheltersNft[]): Promise<NextPayout[]> {
    const { data } = await api.get('/v1/users/me/payouts/next', {
      params: { nfts },
    })
    return data
  },
  async getPayoutsHistory(
    currency: CurrencyEnum,
    month?: number,
  ): Promise<ResponseGetPayoutsHistory> {
    const { data } = await api.get('/v1/users/me/payouts/history', {
      params: { month, currency },
    })
    return data
  },
  async initVerification(
    callbackUrl: string,
    language: string,
  ): Promise<InitVerificationResult> {
    const { data } = await api.post('/v1/users/me/verifications', {
      callbackUrl: callbackUrl,
      language: language,
    })
    return data
  },
  async getBillingAddresses(): Promise<getBillingAddressesResponse> {
    const { data } = await api.get('/v1/users/me/billing-addresses')
    return data
  },
  async getBillingAddressById(
    billingAddressId: string,
  ): Promise<BillingAddress> {
    const { data } = await api.get(
      `/v1/users/me/billing-addresses/${billingAddressId}`,
    )
    return data
  },
  async updateBillingAddress(
    billingAddressId: string,
    newBillingAddress: BillingAddress,
  ): Promise<BillingAddress> {
    const { data } = await api.put(
      '/v1/users/me/billing-addresses/' + billingAddressId,
      newBillingAddress,
    )
    return data
  },
  async createBillingAddress(
    newBillingAddress: BillingAddress,
  ): Promise<BillingAddress> {
    const { data } = await api.post(
      '/v1/users/me/billing-addresses/',
      newBillingAddress,
    )
    return data
  },
  async deleteBillingAddress(billingAddressId: string): Promise<any> {
    const { data } = await api.delete(
      '/v1/users/me/billing-addresses/' + billingAddressId,
    )
    return data
  },
  async createPayoutMethodMultiversx(
    address: string,
    signature: string,
    authToken: string,
    payoutCurrency: PayoutCurrency,
  ): Promise<UserPayoutMethod> {
    const { data } = await api.post('/v1/users/me/payout-methods/multiversx', {
      address,
      signature,
      authToken,
      payoutCurrency,
    })
    return data
  },
  async createPayoutMethodMultiversxV2(
    accessToken: string,
    payoutCurrency: PayoutCurrency,
  ): Promise<UserPayoutMethod> {
    const { data } = await api.post('/v2/users/me/payout-methods/multiversx', {
      accessToken,
      payoutCurrency,
    })
    return data
  },
  async getUserPayoutMethods(): Promise<getUserPayoutMethodsResponse> {
    const { data } = await api.get('/v1/users/me/payout-methods')
    return { pagination: {}, userPayoutMethods: data }
  },
  async updateUserPreference({
    userPayoutMethodId,
    lang,
  }: {
    userPayoutMethodId?: string
    lang?: Language
  }): Promise<UserPreferences> {
    const { data } = await api.put(`/v1/users/me/preferences`, {
      userPayoutMethodId,
      lang,
    })
    return data
  },
  async deleteUserPayoutMethod(userPayoutMethodId: string): Promise<void> {
    await api.delete(`/v1/users/me/payout-methods/${userPayoutMethodId}`)
  },
  async createOrder(
    propertyId: string,
    quantity: number,
    priceDisplayed: number,
    currency: string,
    paymentMethod: string,
    walletId: string,
    billingAddressId: string,
    locale: string,
  ): Promise<CreateOrderResult> {
    const successUrl = `${getBaseUrl(
      locale,
    )}/properties/${propertyId}/checkout-complete/?amount=${quantity}`
    const cancelUrl = `${getBaseUrl(
      locale,
    )}/properties/${propertyId}/checkout/?fractionAmount=${quantity}&walletId=${walletId}&billingAddressId=${billingAddressId}&status=failed`

    const { data } = await api.post(`/v1/users/me/orders`, {
      quantity,
      priceDisplayed,
      currency,
      successUrl,
      cancelUrl,
      paymentMethod,
      walletId,
      billingAddressId,
      propertyId,
    })

    return data
  },
  async getOrders({
    page,
    pageSize,
    orderBy,
    order,
  }: {
    page?: number
    pageSize?: number
    orderBy?: string
    order?: 'DESC' | 'ASC'
  }): Promise<GetOrdersResult> {
    const { data } = await api.get('/v1/users/me/orders', {
      params: {
        page,
        pageSize,
        orderBy,
        order,
        filterBy: ['status'],
        filterOperator: ['!='],
        filter: [[OrderStatus.CANCELLED]],
      },
    })
    return data
  },
  async generateInvestmentSummaryByYearRange({
    startYear,
    endYear,
  }: {
    startYear: number
    endYear: number
  }): Promise<{ blob: Blob; filename: string }> {
    const response = await api.post(
      '/v1/users/me/investment-summary',
      { startYear, endYear },
      { responseType: 'blob' },
    )

    const disposition = response.headers['content-disposition'] as
      | string
      | undefined
    const match = disposition?.match(/filename\*=UTF-8''([^;]+)/i)
    const decoded = match ? decodeURIComponent(match[1]) : null
    const filename = decoded || `investment-summary-${startYear}.pdf`

    return { blob: response.data, filename }
  },
  async createPayoutMethodRevolutIban(
    createRevolutIban: CreateRevolutIban,
  ): Promise<UserPayoutMethod> {
    const { data } = await api.post(
      '/v1/users/me/payout-methods/revolut-iban',
      createRevolutIban,
    )
    return data
  },
  async getRevolutCounterpartyIbanDetails(
    userPayoutMethodId: string,
  ): Promise<RevolutDetails> {
    const { data } = await api.get(
      `/v1/users/me/payout-methods/${userPayoutMethodId}/revolut-iban/details`,
    )
    return data
  },
  async getMePayouts({
    page,
    pageSize,
    daterange,
  }: {
    page?: number
    pageSize?: number
    daterange?: Daterange | null
  }): Promise<PaginatedResponse<MePayout[]>> {
    const { data } = await api.get('/v1/users/me/payouts/transactions', {
      params: {
        page,
        pageSize,
        startAt: daterange?.startAt,
        endAt: daterange?.endAt,
      },
    })
    return data
  },
  async getMePayoutByTransactionId(transactionId: number): Promise<MePayout> {
    const { data } = await api.get(
      `/v1/users/me/payouts/transactions/${transactionId}`,
    )
    return data
  },
  async createBalanceWithdraw(data: CreateBankTransferWithdraw): Promise<void> {
    await api.post('/v1/users/me/balances/withdraw', data)
  },
  async getBalanceTransactions({
    page,
    pageSize,
  }: {
    page: number
    pageSize?: number
  }): Promise<{
    items: any[]
    page: number
    pageSize: number
    maxResults: number
    maxPages: number
  }> {
    const { data } = await api.get('/v1/users/me/balances/transactions', {
      params: { page, pageSize },
    })
    return {
      ...data,
      items: (data.items || []).map((item: any) => ({
        ...item,
        amount: Number(item.amount),
      })),
    }
  },
  async canWithdraw(
    canWithdraw: CanWithdraw,
  ): Promise<{ canWithdraw: boolean }> {
    const { data } = await api.post(
      '/v1/users/me/balances/can-withdraw',
      canWithdraw,
    )
    return data
  },
}

export default UserService
